import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api-client.js';
import { useAuthStore } from '../stores/auth-store.js';
import { formatCurrency, formatMileage } from '@cardealeros/shared';
import { useState, useCallback } from 'react';
import { postToFBMarketplace, type PostResult } from '../services/fb-marketplace-bridge.js';
import { postToCraigslist, type CLPostResult } from '../services/cl-posting-bridge.js';

export default function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [soldPrice, setSoldPrice] = useState('');
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editPrice, setEditPrice] = useState(0);
  const [editDescription, setEditDescription] = useState('');
  const [editCondition, setEditCondition] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState('');

  // FB Marketplace bridge state
  const [fbPosting, setFbPosting] = useState(false);
  const [fbPostResult, setFbPostResult] = useState<PostResult | null>(null);
  // CL posting bridge state
  const [clPosting, setClPosting] = useState(false);
  const [clPostResult, setClPostResult] = useState<CLPostResult | null>(null);


  const { data, isLoading } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => api.get<any>(`/vehicles/${id}`),
  });

  const { data: enrichmentRes } = useQuery({
    queryKey: ['enrichment', id],
    queryFn: () => api.get<any>(`/vehicles/${id}/enrichment`),
    enabled: !!id,
  });

  const { data: pipelineRes } = useQuery({
    queryKey: ['pipeline-status', id],
    queryFn: () => api.get<any>(`/vehicles/${id}/enrich/status`),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      if (data?.status !== 'running') return false;
      const started = new Date(data?.started_at).getTime();
      if (Date.now() - started > 300000) return false; // 5 min max
      return 2000;
    },
  });

  const enrichMutation = useMutation({
    mutationFn: () => api.post(`/vehicles/${id}/enrich`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-status', id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/vehicles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      navigate('/dashboard');
    },
  });

  const [showPostModal, setShowPostModal] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const [postedPlatforms, setPostedPlatforms] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set());
  const [showFormsModal, setShowFormsModal] = useState(false);
  const [formsTab, setFormsTab] = useState<'quick' | 'transaction'>('quick');
  const [buyerInfo, setBuyerInfo] = useState({ name: '', address: '', city: '', state: '', zip: '', drivers_license: '', phone: '' });

  const { data: platformsRes } = useQuery({
    queryKey: ['marketplace-platforms'],
    queryFn: () => api.get<any[]>('/marketplace/platforms'),
  });

  const postMutation = useMutation({
    mutationFn: (platforms?: string[]) => api.post(`/marketplace/vehicles/${id}/post`, { platforms }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['marketplace'] });
      setPostedPlatforms(res.data?.queued ?? []);
      setPostSuccess(true);
      setShowPostModal(false);
      setTimeout(() => setPostSuccess(false), 4000);
    },
  });

  const soldMutation = useMutation({
    mutationFn: (priceCents?: number) => api.post(`/vehicles/${id}/sold`, {
      sold_price_cents: priceCents,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      setShowSoldModal(false);
    },
  });

  const openEditSheet = useCallback(() => {
    if (!data?.data) return;
    setEditPrice(data.data.asking_price_cents ?? 0);
    setEditDescription(data.data.description ?? '');
    setEditCondition(data.data.condition ?? 'good');
    setEditSaved('');
    setShowEditSheet(true);
  }, [data]);

  const saveField = useCallback(async (field: string, value: unknown) => {
    setEditSaving(true);
    try {
      await api.patch(`/vehicles/${id}`, { [field]: value });
      queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
      setEditSaved(field);
      setTimeout(() => setEditSaved(''), 1500);
    } catch { /* best effort */ }
    setEditSaving(false);
  }, [id, queryClient]);

  // Post to FB Marketplace via bridge (extension on desktop, assisted on mobile)
  const handleFBPost = useCallback(async () => {
    if (!id || fbPosting) return;
    setFbPosting(true);
    setFbPostResult(null);

    const result = await postToFBMarketplace(id);
    setFbPostResult(result);
    setFbPosting(false);

    // Auto-clear success message after 8 seconds
    if (result.ok) {
      setTimeout(() => setFbPostResult(null), 8000);
    }
  }, [id, fbPosting]);

  // Post to Craigslist via bridge (extension on desktop, WebView on mobile, clipboard fallback)
  const handleCLPost = useCallback(async () => {
    if (!id || clPosting) return;
    setClPosting(true);
    setClPostResult(null);

    const result = await postToCraigslist(id);
    setClPostResult(result);
    setClPosting(false);

    if (result.ok) {
      setTimeout(() => setClPostResult(null), 10000);
    }
  }, [id, clPosting]);

  if (isLoading) return <div className="page text-center py-20 text-surface-500">Loading...</div>;

  const v = data?.data;
  if (!v) return <div className="page text-center py-20 text-surface-500">Vehicle not found</div>;

  const enrichment = enrichmentRes?.data?.enrichment;
  const marketReport = enrichmentRes?.data?.market_report;
  const photoAnalysis = enrichmentRes?.data?.photo_analysis;
  const pipeline = pipelineRes?.data;
  const pipelineRunning = pipeline?.status === 'running';

  const title = [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ') || 'Unknown Vehicle';
  const photos = v.photos ?? [];
  const features: string[] = typeof v.features === 'string' ? JSON.parse(v.features || '[]') : (v.features ?? []);

  return (
    <div className="page">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="btn-ghost mb-4 -ml-2">&larr; Back</button>

      {/* Photos */}
      {photos.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto mb-4 -mx-4 px-4 pb-2">
          {photos.map((p: any) => (
            <img key={p.id} src={p.url} alt="" className="w-64 sm:w-80 lg:w-96 h-48 sm:h-56 lg:h-64 object-cover rounded-xl flex-shrink-0" />
          ))}
        </div>
      ) : (
        <div className="card h-48 flex items-center justify-center mb-4 text-surface-600">No photos</div>
      )}

      {/* Title + Status */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold">{enrichment?.listing_title || title}</h2>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            v.status === 'active' ? 'bg-green-900/50 text-green-400' :
            v.status === 'sold' ? 'bg-blue-900/50 text-blue-400' :
            v.status === 'pending_sale' ? 'bg-yellow-900/50 text-yellow-400' :
            v.status === 'flagged' ? 'bg-red-900/50 text-red-400' :
            'bg-surface-700 text-surface-400'
          }`}>{v.status === 'flagged' ? 'VIN Mismatch' : v.status}</span>
          {v.asking_price_cents && (
            <span className="text-xl font-bold text-brand-400">{formatCurrency(v.asking_price_cents)}</span>
          )}
          {enrichment?.vehicle_badge && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-900/50 text-brand-400 border border-brand-700">
              {enrichment.vehicle_badge}
            </span>
          )}
          {enrichment?.market_position && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              enrichment.market_position === 'Hot Deal' ? 'bg-green-900/50 text-green-400 border border-green-800' :
              enrichment.market_position === 'Well Priced' ? 'bg-blue-900/50 text-blue-400 border border-blue-800' :
              'bg-yellow-900/50 text-yellow-400 border border-yellow-800'
            }`}>{enrichment.market_position}</span>
          )}
        </div>
      </div>

      {/* ── Quick Actions — right after title ── */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => {
            const connected = (platformsRes?.data ?? []).filter((p: any) => p.is_active);
            if (connected.length === 0) {
              // No platforms connected — go to marketplace setup
              navigate('/marketplace?tab=platforms');
              return;
            }
            // Pre-select all auto-post platforms
            setSelectedPlatforms(new Set(connected.filter((p: any) => p.auto_post).map((p: any) => p.platform)));
            setShowPostModal(true);
          }}
          disabled={postMutation.isPending || postSuccess}
          className={`flex-1 py-3 text-sm rounded-xl font-semibold transition-colors ${postSuccess ? 'bg-green-900/50 text-green-400 border border-green-800' : 'btn-primary'}`}
        >
          {postMutation.isPending ? 'Posting...' : postSuccess ? `Posted to ${postedPlatforms.length} platform${postedPlatforms.length !== 1 ? 's' : ''}!` : 'Post'}
        </button>
        {/* FB Marketplace */}
        <button
          onClick={handleFBPost}
          disabled={fbPosting}
          className={`px-4 py-3 rounded-xl text-sm font-semibold transition-colors border ${
            fbPostResult?.ok ? 'bg-green-900/30 text-green-400 border-green-800' :
            fbPosting ? 'bg-[#1877F2]/20 text-blue-300 border-blue-800 animate-pulse' :
            'bg-[#1877F2]/20 text-[#1877F2] border-[#1877F2]/40 hover:bg-[#1877F2]/30'
          }`}
        >
          {fbPosting ? '...' : fbPostResult?.ok && fbPostResult.method !== 'assisted' ? 'Listed!' : 'FB'}
        </button>
        {/* Craigslist */}
        <button
          onClick={handleCLPost}
          disabled={clPosting}
          className={`px-4 py-3 rounded-xl text-sm font-semibold transition-colors border ${
            clPostResult?.ok ? 'bg-green-900/30 text-green-400 border-green-800' :
            clPosting ? 'bg-orange-500/20 text-orange-300 border-orange-800 animate-pulse' :
            'bg-orange-500/20 text-orange-400 border-orange-500/40 hover:bg-orange-500/30'
          }`}
        >
          {clPosting ? '...' : clPostResult?.ok ? 'Posted!' : 'CL'}
        </button>
        <button
          onClick={() => setShowFormsModal(true)}
          className="px-4 py-3 rounded-xl text-sm font-semibold bg-surface-700 text-surface-300 border border-surface-600 hover:bg-surface-600 transition-colors"
        >
          Forms
        </button>
        {v.status !== 'sold' && (
          <button
            onClick={() => setShowSoldModal(true)}
            className="px-4 py-3 rounded-xl text-sm font-semibold bg-green-900/30 text-green-400 border border-green-800 hover:bg-green-900/50 transition-colors"
          >
            Sold
          </button>
        )}
        {!enrichment && !pipelineRunning && (
          <button
            onClick={() => enrichMutation.mutate()}
            className="px-4 py-3 rounded-xl text-sm font-semibold bg-brand-900/30 text-brand-400 border border-brand-800 hover:bg-brand-900/50 transition-colors"
          >
            AI
          </button>
        )}
      </div>

      {/* FB Marketplace post result */}
      {fbPostResult && (
        <div className={`rounded-xl p-3 mb-4 text-sm ${fbPostResult.ok ? 'bg-green-900/30 border border-green-800' : 'bg-red-900/30 border border-red-800'}`}>
          {fbPostResult.ok ? (
            <div>
              {fbPostResult.method === 'assisted' ? (
                <div>
                  <p className="text-green-400 font-medium">Listing copied to clipboard!</p>
                  <p className="text-surface-400 text-xs mt-1">FB Marketplace is open — paste description, add your photos, and publish.</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-green-400 font-medium">
                    {fbPostResult.method === 'native' ? 'Posted from your phone!' : 'Posted to FB Marketplace!'}
                  </span>
                  {fbPostResult.listingUrl && (
                    <a href={fbPostResult.listingUrl} target="_blank" rel="noopener noreferrer" className="text-[#1877F2] hover:underline text-xs">
                      View listing
                    </a>
                  )}
                  {fbPostResult.banSignals && (
                    <span className="text-yellow-400 text-xs">Warning: {fbPostResult.banSignals.join(', ')}</span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-red-400">{fbPostResult.error}</span>
              <button onClick={() => setFbPostResult(null)} className="text-red-500 text-xs hover:text-red-300">Dismiss</button>
            </div>
          )}
        </div>
      )}

      {/* CL post result */}
      {clPostResult && (
        <div className={`rounded-xl p-3 mb-4 text-sm ${clPostResult.ok ? 'bg-green-900/30 border border-green-800' : 'bg-red-900/30 border border-red-800'}`}>
          {clPostResult.ok ? (
            <div>
              {clPostResult.method === 'assisted' ? (
                <div>
                  <p className="text-green-400 font-medium">Listing copied to clipboard!</p>
                  <p className="text-surface-400 text-xs mt-1">Craigslist is open — paste your listing and add photos.</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-green-400 font-medium">
                    {clPostResult.step === 'complete' ? 'Posted to Craigslist!' : 'Form pre-filled on Craigslist!'}
                  </span>
                  <p className="text-surface-400 text-xs">
                    {clPostResult.step === 'complete'
                      ? 'Your CL account is open — manage your listings there.'
                      : 'Complete the CAPTCHA and payment ($5) to publish. Stay in CL to manage your postings.'}
                  </p>
                  {clPostResult.postUrl && (
                    <a href={clPostResult.postUrl} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline text-xs">
                      View posting
                    </a>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-red-400">{clPostResult.error}</span>
              <button onClick={() => setClPostResult(null)} className="text-red-500 text-xs hover:text-red-300">Dismiss</button>
            </div>
          )}
        </div>
      )}

      {/* Market Intelligence — positioned high for visibility */}
      {marketReport && marketReport.sample_size > 0 && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold text-sm mb-3 text-brand-400">Market Intelligence</h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center">
              <p className="text-green-400 font-bold">{formatCurrency(marketReport.low_price_cents)}</p>
              <p className="text-surface-500 text-[10px]">Low</p>
            </div>
            <div className="text-center">
              <p className="text-brand-400 font-bold text-lg">{formatCurrency(marketReport.median_price_cents)}</p>
              <p className="text-surface-500 text-[10px]">Median</p>
            </div>
            <div className="text-center">
              <p className="text-yellow-400 font-bold">{formatCurrency(marketReport.high_price_cents)}</p>
              <p className="text-surface-500 text-[10px]">High</p>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-surface-500 mb-2">
            <span>{marketReport.sample_size} comparable listings found</span>
            <span className="capitalize">Supply: {marketReport.market_supply}</span>
          </div>
          {marketReport.ai_summary && (
            <p className="text-xs text-surface-400 mt-2 leading-relaxed">{marketReport.ai_summary}</p>
          )}
        </div>
      )}

      {/* AI Pipeline Status */}
      {pipelineRunning && (
        <div className="card p-4 mb-4 border-brand-700 bg-brand-900/10">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-brand-400 text-sm font-medium">AI pipeline processing...</span>
          </div>
        </div>
      )}

      {/* Pipeline Warnings (e.g. VIN vs photo mismatch) */}
      {pipeline?.warnings?.length > 0 && (
        <div className="card p-4 mb-4 border-yellow-700 bg-yellow-900/10">
          {pipeline.warnings.map((w: string, i: number) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <span className="text-yellow-400 mt-0.5 flex-shrink-0">!</span>
              <span className="text-yellow-300">{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* AI-Detected Photo Details */}
      {photoAnalysis && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold text-sm mb-3 text-brand-400">AI Photo Analysis</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {photoAnalysis.exterior_color && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border border-surface-600"
                  style={{ backgroundColor: photoAnalysis.exterior_color_hex || '#888' }}
                />
                <div>
                  <span className="text-surface-500 text-xs">Exterior</span>
                  <p className="font-medium text-xs">{photoAnalysis.exterior_color}</p>
                </div>
              </div>
            )}
            {photoAnalysis.interior_color && (
              <div>
                <span className="text-surface-500 text-xs">Interior</span>
                <p className="font-medium text-xs">
                  {photoAnalysis.interior_color} {photoAnalysis.interior_material ? `(${photoAnalysis.interior_material})` : ''}
                </p>
              </div>
            )}
            {photoAnalysis.body_condition && (
              <div>
                <span className="text-surface-500 text-xs">Condition</span>
                <p className="font-medium text-xs capitalize">{photoAnalysis.body_condition}</p>
              </div>
            )}
            {photoAnalysis.wheel_type && (
              <div>
                <span className="text-surface-500 text-xs">Wheels</span>
                <p className="font-medium text-xs">{photoAnalysis.wheel_type}</p>
              </div>
            )}
          </div>
          {photoAnalysis.damage_detected && photoAnalysis.damage_details?.length > 0 && (
            <div className="mt-3 p-2 bg-red-900/20 rounded-lg">
              <p className="text-red-400 text-xs font-medium mb-1">Damage Detected</p>
              <ul className="text-xs text-red-300/80 space-y-0.5">
                {photoAnalysis.damage_details.map((d: string, i: number) => (
                  <li key={i}>- {d}</li>
                ))}
              </ul>
            </div>
          )}
          {photoAnalysis.visible_features?.length > 0 && (
            <div className="mt-3">
              <p className="text-surface-500 text-xs mb-1">Detected Features</p>
              <div className="flex flex-wrap gap-1">
                {photoAnalysis.visible_features.map((f: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-surface-700 rounded text-xs text-surface-300">{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vehicle Specs Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'VIN', value: v.vin },
          { label: 'Miles', value: formatMileage(v.mileage) },
          { label: 'Engine', value: v.engine },
          { label: 'Transmission', value: v.transmission },
          { label: 'Drivetrain', value: v.drivetrain },
          { label: 'Fuel', value: v.fuel_type },
          { label: 'Body', value: v.body_style },
          { label: 'Doors', value: v.doors },
          { label: 'Exterior', value: v.exterior_color },
          { label: 'Interior', value: v.interior_color },
          { label: 'Condition', value: v.condition },
          { label: 'Stock #', value: v.stock_number },
        ].map(({ label, value }) => (
          <div key={label} className="card p-3">
            <p className="text-xs text-surface-500">{label}</p>
            <p className="text-sm font-medium mt-0.5">{value ?? '\u2014'}</p>
          </div>
        ))}
      </div>

      {/* Pricing */}
      <div className="card p-4 mb-4">
        <h3 className="font-semibold text-sm mb-3">Pricing</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-surface-500">Purchase Price</span>
            <span>{formatCurrency(v.purchase_price_cents)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-500">Asking Price</span>
            <span className="font-semibold text-brand-400">{formatCurrency(v.asking_price_cents)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-500">Market Value</span>
            <span>{formatCurrency(v.market_value_cents)}</span>
          </div>
          {enrichment?.suggested_price_range_low_cents > 0 && (
            <div className="flex justify-between">
              <span className="text-surface-500">AI Suggested Range</span>
              <span className="text-brand-300">
                {formatCurrency(enrichment.suggested_price_range_low_cents)} – {formatCurrency(enrichment.suggested_price_range_high_cents)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* AI Highlights */}
      {enrichment?.highlights?.length > 0 && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold text-sm mb-3 text-brand-400">Key Selling Points</h3>
          <ul className="space-y-1.5">
            {enrichment.highlights.map((h: string, i: number) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-green-400 mt-0.5">+</span>
                <span className="text-surface-300">{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Features List */}
      {features.length > 0 && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold text-sm mb-3">Features</h3>
          <div className="flex flex-wrap gap-1.5">
            {features.map((f: string, i: number) => (
              <span key={i} className="px-2.5 py-1 bg-surface-700 rounded-lg text-xs text-surface-300">{f}</span>
            ))}
          </div>
        </div>
      )}

      {/* AI Description */}
      {v.description && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold text-sm mb-3">Listing Description</h3>
          <p className="text-sm text-surface-400 leading-relaxed whitespace-pre-wrap">{v.description}</p>
        </div>
      )}

      {/* Ready-to-Post Copy */}
      {enrichment?.facebook_post && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold text-sm mb-3">Ready-to-Post Copy</h3>
          <div className="space-y-3">
            <div>
              <p className="text-surface-500 text-xs mb-1">Facebook</p>
              <div className="bg-surface-700/50 rounded-lg p-3 text-xs text-surface-300 whitespace-pre-wrap">
                {enrichment.facebook_post}
              </div>
            </div>
            {enrichment.hashtags?.length > 0 && (
              <p className="text-xs text-brand-400">
                {enrichment.hashtags.map((h: string) => `#${h}`).join(' ')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2 mb-6">
        <button
          onClick={() => {
            if (confirm('Delete this vehicle and all its photos? This cannot be undone.')) {
              deleteMutation.mutate();
            }
          }}
          disabled={deleteMutation.isPending}
          className="btn-ghost w-full text-sm text-red-400 hover:text-red-300"
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Delete Vehicle'}
        </button>
      </div>

      {/* Post Platform Picker Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-surface-800 rounded-t-2xl sm:rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-1">Post to</h3>
            <p className="text-surface-500 text-xs mb-4">Select where to list this vehicle</p>

            {(() => {
              const connected = (platformsRes?.data ?? []).filter((p: any) => p.is_active);
              if (connected.length === 0) {
                return (
                  <div className="text-center py-6">
                    <p className="text-surface-400 text-sm mb-3">No platforms connected yet</p>
                    <button onClick={() => { setShowPostModal(false); navigate('/marketplace?tab=platforms'); }} className="btn-primary px-6">
                      Connect Platforms
                    </button>
                  </div>
                );
              }
              const platformLabels: Record<string, { name: string; color: string }> = {
                facebook_page: { name: 'Facebook Page', color: 'bg-[#1877F2]' },
                facebook_marketplace: { name: 'FB Marketplace', color: 'bg-[#1877F2]' },
                instagram: { name: 'Instagram', color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
                craigslist: { name: 'Craigslist', color: 'bg-orange-500' },
                tiktok: { name: 'TikTok', color: 'bg-black' },
                offerup: { name: 'OfferUp', color: 'bg-green-600' },
              };

              return (
                <>
                  <div className="space-y-2 mb-5">
                    {connected.map((p: any) => {
                      const info = platformLabels[p.platform] ?? { name: p.platform, color: 'bg-surface-600' };
                      const checked = selectedPlatforms.has(p.platform);
                      return (
                        <button
                          key={p.platform}
                          onClick={() => {
                            const next = new Set(selectedPlatforms);
                            if (checked) next.delete(p.platform); else next.add(p.platform);
                            setSelectedPlatforms(next);
                          }}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                            checked ? 'border-brand-500 bg-brand-500/10' : 'border-surface-700 hover:border-surface-600'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg ${info.color} flex items-center justify-center text-white text-xs font-bold`}>
                            {info.name[0]}
                          </div>
                          <span className="flex-1 text-left text-sm font-medium">{info.name}</span>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                            checked ? 'border-brand-500 bg-brand-500' : 'border-surface-600'
                          }`}>
                            {checked && (
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (selectedPlatforms.size > 0) {
                          postMutation.mutate([...selectedPlatforms]);
                        }
                      }}
                      disabled={selectedPlatforms.size === 0 || postMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {postMutation.isPending ? 'Posting...' : `Post to ${selectedPlatforms.size} Platform${selectedPlatforms.size !== 1 ? 's' : ''}`}
                    </button>
                    <button onClick={() => setShowPostModal(false)} className="btn-ghost px-4">
                      Cancel
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Edit FAB */}
      {v.status !== 'sold' && !showEditSheet && (
        <button
          onClick={openEditSheet}
          className="fixed bottom-20 right-4 w-14 h-14 bg-brand-600 hover:bg-brand-700 rounded-full shadow-lg flex items-center justify-center z-40 transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      )}

      {/* Edit Bottom Sheet */}
      {showEditSheet && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end">
          <div className="bg-surface-800 rounded-t-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Quick Edit</h3>
              <button onClick={() => setShowEditSheet(false)} className="text-surface-400 hover:text-white p-1">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Price Slider */}
            <div className="mb-6">
              <label className="text-xs text-surface-400 uppercase tracking-wider block mb-2">Asking Price</label>
              <p className="text-3xl font-bold text-brand-400 mb-3">{formatCurrency(editPrice)}</p>
              <input
                type="range"
                min={50000}
                max={marketReport?.high_price_cents ? Math.max(marketReport.high_price_cents * 1.2, 500000) : 5000000}
                step={25000}
                value={editPrice}
                onChange={(e) => setEditPrice(parseInt(e.target.value))}
                onMouseUp={() => saveField('asking_price_cents', editPrice)}
                onTouchEnd={() => saveField('asking_price_cents', editPrice)}
                className="w-full accent-brand-500"
              />
              <div className="flex justify-between text-xs text-surface-500 mt-1">
                <span>$500</span>
                {marketReport?.street_price_cents ? (
                  <span className="text-brand-400">Street: {formatCurrency(marketReport.street_price_cents)}</span>
                ) : null}
                <span>{formatCurrency(marketReport?.high_price_cents ? Math.round(marketReport.high_price_cents * 1.2) : 5000000)}</span>
              </div>
              {editSaved === 'asking_price_cents' && (
                <p className="text-green-400 text-xs mt-1">Price saved!</p>
              )}
            </div>

            {/* Condition */}
            <div className="mb-6">
              <label className="text-xs text-surface-400 uppercase tracking-wider block mb-2">Condition</label>
              <div className="grid grid-cols-4 gap-2">
                {['excellent', 'good', 'fair', 'rough'].map((c) => (
                  <button
                    key={c}
                    onClick={() => { setEditCondition(c); saveField('condition', c); }}
                    className={`py-2 rounded-xl text-xs font-medium capitalize transition-colors border ${
                      editCondition === c
                        ? 'border-brand-500 bg-brand-500/10 text-brand-400'
                        : 'border-surface-700 bg-surface-700 text-surface-400'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              {editSaved === 'condition' && (
                <p className="text-green-400 text-xs mt-1">Condition saved!</p>
              )}
            </div>

            {/* Description */}
            <div className="mb-6">
              <label className="text-xs text-surface-400 uppercase tracking-wider block mb-2">Description</label>
              <textarea
                className="input w-full text-sm resize-none"
                rows={4}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Describe your vehicle..."
              />
              <button
                onClick={() => saveField('description', editDescription)}
                disabled={editSaving}
                className="btn-primary mt-2 text-sm !py-2 !px-4"
              >
                {editSaved === 'description' ? 'Saved!' : editSaving ? 'Saving...' : 'Save Description'}
              </button>
            </div>

            <button
              onClick={() => setShowEditSheet(false)}
              className="btn-ghost w-full text-sm"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Print Forms Modal */}
      {showFormsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-surface-800 rounded-t-2xl sm:rounded-2xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Print Forms</h3>
              <button onClick={() => setShowFormsModal(false)} className="text-surface-400 hover:text-white p-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tab switcher */}
            <div className="flex gap-1 mb-4 bg-surface-700 rounded-xl p-1">
              <button
                onClick={() => setFormsTab('quick')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${formsTab === 'quick' ? 'bg-brand-600 text-white' : 'text-surface-400'}`}
              >
                Quick Print
              </button>
              <button
                onClick={() => setFormsTab('transaction')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${formsTab === 'transaction' ? 'bg-brand-600 text-white' : 'text-surface-400'}`}
              >
                Transaction Forms
              </button>
            </div>

            {formsTab === 'quick' ? (
              <div className="space-y-2">
                <p className="text-surface-500 text-xs mb-3">No buyer info needed — opens pre-filled and ready to print.</p>
                <button
                  onClick={() => window.open(`${import.meta.env.PROD ? 'https://cardealeros-api.bowling-anthony.workers.dev' : ''}/v1/forms/vehicle/${id}/info-sheet`, '_blank')}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border border-surface-700 hover:border-brand-500 hover:bg-brand-500/5 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-900/40 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Vehicle Info Sheet</p>
                    <p className="text-surface-500 text-xs">Window sticker with all specs + price</p>
                  </div>
                </button>
                <button
                  onClick={() => window.open(`${import.meta.env.PROD ? 'https://cardealeros-api.bowling-anthony.workers.dev' : ''}/v1/forms/vehicle/${id}/buyers-guide?warranty=false`, '_blank')}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border border-surface-700 hover:border-brand-500 hover:bg-brand-500/5 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-yellow-900/40 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Buyers Guide (As-Is)</p>
                    <p className="text-surface-500 text-xs">FTC required — sold as-is, no warranty</p>
                  </div>
                </button>
                <button
                  onClick={() => window.open(`${import.meta.env.PROD ? 'https://cardealeros-api.bowling-anthony.workers.dev' : ''}/v1/forms/vehicle/${id}/buyers-guide?warranty=true`, '_blank')}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border border-surface-700 hover:border-brand-500 hover:bg-brand-500/5 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-900/40 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Buyers Guide (Warranty)</p>
                    <p className="text-surface-500 text-xs">FTC required — with warranty coverage</p>
                  </div>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-surface-500 text-xs mb-1">Enter buyer info to generate pre-filled forms.</p>

                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <input className="input w-full text-sm" placeholder="Buyer Name *" value={buyerInfo.name} onChange={(e) => setBuyerInfo({ ...buyerInfo, name: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <input className="input w-full text-sm" placeholder="Address" value={buyerInfo.address} onChange={(e) => setBuyerInfo({ ...buyerInfo, address: e.target.value })} />
                  </div>
                  <input className="input w-full text-sm" placeholder="City" value={buyerInfo.city} onChange={(e) => setBuyerInfo({ ...buyerInfo, city: e.target.value })} />
                  <div className="flex gap-2">
                    <input className="input w-16 text-sm" placeholder="ST" maxLength={2} value={buyerInfo.state} onChange={(e) => setBuyerInfo({ ...buyerInfo, state: e.target.value.toUpperCase() })} />
                    <input className="input flex-1 text-sm" placeholder="ZIP" value={buyerInfo.zip} onChange={(e) => setBuyerInfo({ ...buyerInfo, zip: e.target.value })} />
                  </div>
                  <input className="input w-full text-sm" placeholder="Phone" value={buyerInfo.phone} onChange={(e) => setBuyerInfo({ ...buyerInfo, phone: e.target.value })} />
                  <input className="input w-full text-sm" placeholder="Driver's License #" value={buyerInfo.drivers_license} onChange={(e) => setBuyerInfo({ ...buyerInfo, drivers_license: e.target.value })} />
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    onClick={async () => {
                      const formApiBase = import.meta.env.PROD ? 'https://cardealeros-api.bowling-anthony.workers.dev' : '';
                      const authToken = useAuthStore.getState().token;
                      const res = await fetch(`${formApiBase}/v1/forms/bill-of-sale`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken ?? ''}` },
                        body: JSON.stringify({
                          vehicle_id: id,
                          customer: buyerInfo,
                          deal: { sale_price_cents: v.asking_price_cents ?? 0, payment_type: 'Cash', sale_date: new Date().toLocaleDateString('en-US') },
                        }),
                      });
                      const html = await res.text();
                      const w = window.open('', '_blank');
                      if (w) { w.document.write(html); w.document.close(); }
                    }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-surface-700 hover:border-brand-500 hover:bg-brand-500/5 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-brand-900/40 flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Bill of Sale</p>
                      <p className="text-surface-500 text-xs">Complete BOS with vehicle, buyer & seller info</p>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      const customer = encodeURIComponent(JSON.stringify(buyerInfo));
                      window.open(`${import.meta.env.PROD ? 'https://cardealeros-api.bowling-anthony.workers.dev' : ''}/v1/forms/vehicle/${id}/odometer?customer=${customer}`, '_blank');
                    }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-surface-700 hover:border-brand-500 hover:bg-brand-500/5 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-purple-900/40 flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Odometer Disclosure</p>
                      <p className="text-surface-500 text-xs">Federal mileage statement — required by law</p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sold Modal */}
      {showSoldModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-800 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">Mark Vehicle Sold</h3>
            <p className="text-surface-400 text-sm mb-4">
              This will mark the vehicle as sold and remove all active listings across connected platforms.
            </p>
            <label className="text-xs text-surface-500 mb-1 block">Sale Price (optional)</label>
            <input
              type="number"
              className="input w-full mb-4"
              placeholder="e.g. 15000"
              value={soldPrice}
              onChange={(e) => setSoldPrice(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const priceCents = soldPrice ? Math.round(parseFloat(soldPrice) * 100) : undefined;
                  soldMutation.mutate(priceCents);
                }}
                disabled={soldMutation.isPending}
                className="btn-primary flex-1"
              >
                {soldMutation.isPending ? 'Processing...' : 'Confirm Sold'}
              </button>
              <button
                onClick={() => setShowSoldModal(false)}
                className="btn-ghost flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
